import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Calendar, Mail, CheckCircle2, Clock, MessageCircle } from "lucide-react";
import { fadeUp, stagger, AnimatedSection } from "@/lib/animations";

export default function BookCallPage() {
  const benefits = [
    "Audit digitale personalizzato della tua presenza online",
    "Valutazione dell'idoneità ai fondi per il marketing e per le AI",
    "Strategia di crescita personalizzata per la tua missione",
    "Nessun impegno — solo consigli onesti e attuabili",
  ];

  return (
    <div data-testid="book-call-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="book-call-hero">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left - Info */}
            <AnimatedSection>
              <motion.div variants={fadeUp}>
                <Badge className="bg-[#D85A30]/10 text-[#D85A30] border-[#D85A30]/20 hover:bg-[#D85A30]/15 mb-6 px-4 py-1.5 rounded-full">
                  <span className="kicker">Chiamata Strategica Gratuita</span>
                </Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-semibold text-[#2C2C2A] leading-[1.1] mb-6">
                Parliamo della tua{" "}
                <span className="text-[#D85A30]">missione.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] leading-relaxed mb-8">
                Prenota una chiamata strategica gratuita e senza impegno con il nostro team. Analizzeremo la 
                presenza digitale della tua nonprofit e ti mostreremo esattamente come raggiungere più persone.
              </motion.p>

              <motion.div variants={fadeUp} className="space-y-4 mb-10">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`benefit-${i}`}>
                    <CheckCircle2 className="w-5 h-5 text-[#2C2C2A] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#888780]">{b}</p>
                  </div>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-col gap-4 text-sm text-[#888780]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#085041]/5 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#2C2C2A]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2A]">Chiamata di 30 minuti</p>
                    <p className="text-xs">Rapida, mirata e concreta</p>
                  </div>
                </div>
                <a href="mailto:hello@givenda.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                  <div className="w-10 h-10 rounded-xl bg-[#085041]/5 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#2C2C2A]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2A]">hello@givenda.com</p>
                    <p className="text-xs">Preferisci l'email? Scrivici quando vuoi</p>
                  </div>
                </a>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#085041]/5 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#2C2C2A]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2C2A]">Risposta entro 24 ore</p>
                    <p className="text-xs">Lun-Ven, 9:00-18:00 CET</p>
                  </div>
                </div>
              </motion.div>
            </AnimatedSection>

            {/* Right - Quick actions */}
            <AnimatedSection>
              <motion.div variants={fadeUp}>
                <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)]" data-testid="contact-actions-card">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-[#085041]/5 flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-8 h-8 text-[#085041]" />
                    </div>
                    <h3 className="text-2xl font-semibold text-[#2C2C2A] mb-3">Prenota direttamente</h3>
                    <p className="text-[#888780] leading-relaxed mb-8">
                      Scegli l'orario che preferisci e parla con un nostro esperto. La chiamata è gratuita e senza impegno.
                    </p>
                    <div className="space-y-3">
                      <a href="mailto:hello@givenda.com?subject=Richiesta%20Chiamata%20Strategica%20Gratuita" className="block">
                        <Button className="w-full rounded-full h-12 bg-[#1D9E75] hover:bg-[#17845F] text-white font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="book-email-button">
                          Scrivici per Prenotare
                          <Mail className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                      <a href="mailto:hello@givenda.com" className="block">
                        <Button variant="outline" className="w-full rounded-full h-12 border-[#085041]/20 text-[#2C2C2A] hover:bg-[#085041]/5 font-medium" data-testid="book-contact-button">
                          Hai Domande? Contattaci
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </div>
                    <p className="text-xs text-[#888780] mt-6">
                      100% gratuito. Nessuna carta di credito richiesta. Rispondiamo entro 24 ore.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-32 bg-[#F1EFE8]/40" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12">
            <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Come Funziona</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-semibold text-[#2C2C2A] leading-tight mb-4">
              Semplice, veloce, senza impegno
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-[#888780] max-w-xl mx-auto">
              Tre passi per avviare la tua crescita digitale.
            </motion.p>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Scrivici", desc: "Invia una email a hello@givenda.com con una breve descrizione della tua organizzazione.", icon: Mail },
              { step: "02", title: "Fissiamo una Call", desc: "Il nostro team risponde entro 24 ore per concordare data e orario della chiamata gratuita.", icon: Calendar },
              { step: "03", title: "Ricevi il tuo Piano", desc: "In 30 minuti ti consegniamo un'analisi personalizzata e i prossimi passi concreti per crescere.", icon: CheckCircle2 },
            ].map(({ step, title, desc, icon: Icon }) => (
              <motion.div key={step} variants={fadeUp}>
                <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] h-full">
                  <CardContent className="p-8 text-center">
                    <span className="text-4xl font-bold text-[#2C2C2A]/5 block mb-4" style={{ fontFamily: "'Fraunces', serif" }}>{step}</span>
                    <div className="w-12 h-12 rounded-2xl bg-[#D85A30]/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-[#D85A30]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#2C2C2A] mb-2">{title}</h3>
                    <p className="text-sm text-[#888780] leading-relaxed">{desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatedSection>

          <AnimatedSection className="mt-10 text-center">
            <motion.div variants={fadeUp}>
              <a href="mailto:hello@givenda.com?subject=Richiesta%20Chiamata%20Strategica%20Gratuita">
                <Button className="rounded-full px-10 h-12 text-base bg-[#1D9E75] hover:bg-[#17845F] text-white font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid="how-it-works-cta">
                  Inizia Ora — È Gratuito
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
